/**
 * @fileoverview Pure CPA calculator math and cited 2026 benchmark bands.
 * @description Converts realized spend, funnel rates, or target economics into
 * cost-per-acquisition figures without I/O so the UI and unit tests share one
 * implementation. Published averages and medians stay at source precision.
 * @module components/resources/calculators/CpaCalculator.logic
 * @exports {CpaMode} CpaMode - Realized, funnel, or target input mode.
 * @exports {FunnelPath} FunnelPath - CPC or CPM funnel entry path.
 * @exports {CpaInputs} CpaInputs - Calculator input bag.
 * @exports {CpaResults} CpaResults - Derived CPA figures.
 * @exports {CpaVerdict} CpaVerdict - Qualitative read against a ceiling.
 * @exports {CpaBenchmark} CpaBenchmark - Cited platform CPA/CPL band.
 * @exports {CpaPublishedStatistic} CpaPublishedStatistic - Average vs median for a cited figure.
 * @exports {CPA_BENCHMARKS} CPA_BENCHMARKS - Cited 2026 / latest 2025 bands.
 * @exports {Function} calculateCpa - Evaluate the active mode and overlays.
 * @exports {Function} tightestMediaCpaCeiling - Minimum of break-even and ROAS media ceilings.
 * @exports {Function} resolveCpaVerdictBasis - Which media ceiling verdict copy may name.
 * @exports {Function} resolveSensitivityCpc - Active-mode CPC for the sensitivity chart, or null.
 * @exports {Function} isMedianInsideSpan - Whether a cited published figure sits in its span.
 * @exports {Function} publishedStatisticLabel - User-facing average/median label.
 * @exports {Function} clampNumericInput - Parse and clamp a numeric field.
 * @exports {Function} isNonNegativeNumber - Finite number ≥ 0 (zero-valued costs and ceilings).
 * @exports {Function} buildCpaExportRows - Mode-gated CSV rows for the widget.
 * @exports {Function} formatCpaVerdictForExport - Basis-aware verdict cell.
 * @exports {CpaExportSnapshot} CpaExportSnapshot - Inputs plus results for CSV export.
 */

/**
 * Calculator input mode. Realized uses spend ÷ conversions. Funnel derives CPA
 * from CPC or CPM × CTR × CVR. Target computes break-even and LTV/ROAS ceilings.
 *
 * @typedef {'realized' | 'funnel' | 'target'} CpaMode
 */
export type CpaMode = 'realized' | 'funnel' | 'target'

/**
 * Funnel entry path. CPC path is spend-per-click ÷ conversion rate. CPM path
 * compounds impression cost through click-through and conversion rates.
 *
 * @typedef {'cpc' | 'cpm'} FunnelPath
 */
export type FunnelPath = 'cpc' | 'cpm'

/**
 * Inputs for {@link calculateCpa}. Unused fields for the active mode are ignored.
 *
 * @interface CpaInputs
 * @property {CpaMode} mode - Which formula family to evaluate.
 * @property {FunnelPath} [funnelPath='cpc'] - Funnel entry path when mode is funnel.
 * @property {number} [adSpend] - Total attributed ad spend for the period.
 * @property {number} [conversions] - Counted conversions (same event as the spend).
 * @property {number} [cpc] - Cost per click.
 * @property {number} [cpm] - Cost per thousand impressions.
 * @property {number} [ctrPct] - Click-through rate as a percent (2 = 2%).
 * @property {number} [cvrPct] - Click-to-conversion rate as a percent.
 * @property {number} [aov] - Average order value (or revenue per conversion).
 * @property {number} [grossMarginPct] - Gross margin as a percent of AOV.
 * @property {number} [ltv] - Expected customer lifetime value.
 * @property {number} [targetLtvCacRatio] - Target LTV:CAC multiple (e.g. 3).
 * @property {number} [targetRoas] - Target return on ad spend multiple.
 */
export interface CpaInputs {
  mode: CpaMode
  funnelPath?: FunnelPath
  adSpend?: number
  conversions?: number
  cpc?: number
  cpm?: number
  ctrPct?: number
  cvrPct?: number
  aov?: number
  grossMarginPct?: number
  ltv?: number
  targetLtvCacRatio?: number
  targetRoas?: number
}

/**
 * Derived outputs. Null means the required inputs for that figure were missing
 * or invalid — the UI should hide the card rather than invent a zero. An
 * explicit 0% margin is a real $0 break-even, not a missing value.
 *
 * @interface CpaResults
 * @property {number | null} cpa - Realized or funnel-implied CPA.
 * @property {number | null} breakEvenCpa - AOV × gross margin (contribution ceiling; $0 when margin is 0%).
 * @property {number | null} targetCpaFromLtv - LTV ÷ target LTV:CAC — a target CAC, not media-only CPA.
 * @property {number | null} targetCpaFromRoas - AOV ÷ target ROAS.
 * @property {number | null} impliedRoas - AOV ÷ CPA when both exist.
 * @property {number | null} impliedCpc - Funnel-implied CPC when CPM + CTR exist.
 * @property {number | null} headroom - Break-even CPA minus realized CPA.
 * @property {CpaVerdict} verdict - Qualitative read against a media-CPA ceiling.
 * @property {CpaVerdictBasis} verdictBasis - Which media ceiling the verdict compared against.
 */
export interface CpaResults {
  cpa: number | null
  breakEvenCpa: number | null
  targetCpaFromLtv: number | null
  targetCpaFromRoas: number | null
  impliedRoas: number | null
  impliedCpc: number | null
  headroom: number | null
  verdict: CpaVerdict
  verdictBasis: CpaVerdictBasis
}

/**
 * Qualitative verdict for the result panel.
 *
 * @typedef {'profitable' | 'unprofitable' | 'on_target' | 'insufficient'} CpaVerdict
 */
export type CpaVerdict = 'profitable' | 'unprofitable' | 'on_target' | 'insufficient'

/**
 * Which media-CPA ceiling {@link classifyCpaVerdict} compared against.
 * Break-even is preferred when AOV and margin exist; otherwise the ROAS
 * target. LTV ÷ LTV:CAC is a fully loaded CAC and is never this basis.
 *
 * @typedef {'break_even' | 'target' | 'none'} CpaVerdictBasis
 */
export type CpaVerdictBasis = 'break_even' | 'target' | 'none'

/**
 * Distribution statistic a publisher reported for the cited central figure.
 *
 * @typedef {'average' | 'median'} CpaPublishedStatistic
 */
export type CpaPublishedStatistic = 'average' | 'median'

/**
 * A cited platform / conversion-definition CPA band.
 *
 * @interface CpaBenchmark
 * @property {string} id - Stable id for keys and CSV rows.
 * @property {string} label - Human label including the conversion definition.
 * @property {number} low - Published low of the typical band.
 * @property {number} median - Published central figure (average or median; see statistic).
 * @property {CpaPublishedStatistic} statistic - Whether `median` is an average or a median.
 * @property {number} high - Published high of the typical band.
 * @property {string} source - Publisher + year, kept at the source's own wording.
 * @property {string} note - Definition / mix caveat so bands are not mixed.
 */
export interface CpaBenchmark {
  id: string
  label: string
  low: number
  median: number
  statistic: CpaPublishedStatistic
  high: number
  source: string
  note: string
}

/**
 * Cited 2026 (and latest 2025 restated) CPA / CPL bands.
 *
 * WordStream/LocaliQ figures are cost-per-lead on search/social lead campaigns.
 * Triple Whale figures, as restated by 2026 recaps, are ecommerce *purchase* CPA.
 * Those are different conversion events — the calculator never averages them.
 *
 * @constant
 */
export const CPA_BENCHMARKS: readonly CpaBenchmark[] = [
  {
    id: 'google-search-cpl',
    label: 'Google Ads search · cost per lead',
    low: 26.84,
    median: 66.69,
    statistic: 'average',
    high: 131.63,
    source: 'WordStream / LocaliQ, Search Advertising Benchmarks 2026',
    note: 'Cross-industry CPL. Arts & Entertainment is the published floor; Attorneys & Legal Services the ceiling. Not a purchase CPA.',
  },
  {
    id: 'meta-lead-cpl',
    label: 'Meta lead campaigns · cost per lead',
    low: 3.16,
    median: 27.66,
    statistic: 'average',
    high: 76.71,
    source: 'WordStream Facebook Ads Benchmarks 2025 (restated in the 2026 costs guide)',
    note: 'Lead-form CPL. Restaurants & Food is the published floor; Dentists & Dental Services the ceiling. Compare only to lead-optimized campaigns.',
  },
  {
    id: 'meta-ecom-purchase',
    label: 'Meta · ecommerce purchase CPA',
    low: 29.99,
    median: 38.19,
    statistic: 'median',
    high: 49.48,
    source: 'Triple Whale 2025 ecommerce cohort (updated Feb 2026)',
    note: 'Purchase CPA across ~35k ecommerce brands. Lifestyle & Boutique is cheapest; Electronics the dearest in the cited cut.',
  },
  {
    id: 'google-ecom-purchase',
    label: 'Google Ads · ecommerce purchase CPA',
    low: 25.15,
    median: 23.74,
    statistic: 'median',
    high: 35.37,
    source: 'Triple Whale 2025 ecommerce cohort (updated Feb 2026)',
    note: 'Purchase CPA. Named-industry rows run $25.15–$35.37; the $23.74 figure is the all-industry median (cheaper verticals are not broken out). Not Google search CPL.',
  },
] as const

const POSITIVE_EPSILON = 1e-9
/** Inclusive relative band around a media ceiling that still counts as on-target. */
const ON_TARGET_RELATIVE_BAND = 0.02

/**
 * Returns true when a value is a finite number greater than zero.
 *
 * @param {number | null | undefined} value - Candidate numeric input.
 * @returns {boolean} Whether the value can enter a denominator or numerator.
 */
export function isPositiveNumber(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > POSITIVE_EPSILON
}

/**
 * Returns true when a value is a finite number at or above zero.
 *
 * Used for contribution ceilings: an explicit 0% margin is a $0 break-even,
 * which is different from a missing margin (null).
 *
 * @param {number | null | undefined} value - Candidate numeric input.
 * @returns {boolean} Whether the value is a usable non-negative finite number.
 */
export function isNonNegativeNumber(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
}

/**
 * Realized CPA: spend ÷ conversions.
 *
 * @param {number | null | undefined} adSpend - Attributed ad spend. Explicit $0 is a real $0 CPA; missing stays null.
 * @param {number | null | undefined} conversions - Counted conversions.
 * @returns {number | null} CPA, or null when spend is missing/negative or conversions are not positive.
 */
export function realizedCpa(
  adSpend: number | null | undefined,
  conversions: number | null | undefined,
): number | null {
  if (!isNonNegativeNumber(adSpend) || !isPositiveNumber(conversions)) return null
  return adSpend / conversions
}

/**
 * Funnel CPA from cost-per-click and click-to-conversion rate.
 *
 * CPA = CPC ÷ (CVR% / 100)
 *
 * @param {number | null | undefined} cpc - Cost per click. Explicit $0 is a real $0 CPA; missing stays null.
 * @param {number | null | undefined} cvrPct - Conversion rate as a percent.
 * @returns {number | null} Implied CPA, or null when CPC is missing/negative or CVR is not positive.
 */
export function funnelCpaFromCpc(
  cpc: number | null | undefined,
  cvrPct: number | null | undefined,
): number | null {
  if (!isNonNegativeNumber(cpc) || !isPositiveNumber(cvrPct)) return null
  return cpc / (cvrPct / 100)
}

/**
 * Funnel CPA from CPM, CTR, and CVR.
 *
 * CPA = CPM ÷ (CTR_decimal × CVR_decimal × 1000)
 *
 * @param {number | null | undefined} cpm - Cost per thousand impressions. Explicit $0 is a real $0 CPA; missing stays null.
 * @param {number | null | undefined} ctrPct - Click-through rate as a percent.
 * @param {number | null | undefined} cvrPct - Click-to-conversion rate as a percent.
 * @returns {number | null} Implied CPA, or null when CPM is missing/negative or a rate is not positive.
 */
export function funnelCpaFromCpm(
  cpm: number | null | undefined,
  ctrPct: number | null | undefined,
  cvrPct: number | null | undefined,
): number | null {
  if (!isNonNegativeNumber(cpm) || !isPositiveNumber(ctrPct) || !isPositiveNumber(cvrPct)) {
    return null
  }
  const conversionsPerThousand = (ctrPct / 100) * (cvrPct / 100) * 1000
  if (conversionsPerThousand <= POSITIVE_EPSILON) return null
  return cpm / conversionsPerThousand
}

/**
 * Implied CPC from CPM and CTR: CPC = CPM ÷ (10 × CTR%).
 *
 * @param {number | null | undefined} cpm - Cost per thousand impressions. Explicit $0 is a real $0 CPC; missing stays null.
 * @param {number | null | undefined} ctrPct - Click-through rate as a percent.
 * @returns {number | null} Implied CPC, or null when CPM is missing/negative or CTR is not positive.
 */
export function impliedCpcFromCpm(
  cpm: number | null | undefined,
  ctrPct: number | null | undefined,
): number | null {
  if (!isNonNegativeNumber(cpm) || !isPositiveNumber(ctrPct)) return null
  return cpm / (10 * ctrPct)
}

/**
 * Break-even CPA: the most you can pay per conversion and still cover COGS.
 * Break-even CPA = AOV × (gross margin % / 100).
 *
 * An explicit 0% margin is a $0 ceiling. A missing margin stays null so
 * `?? 0` at the call site cannot invent a zero-margin business. Margin above
 * 100% is economically invalid and stays null rather than inventing a CPA
 * larger than AOV (the widget also clamps the field; this is the logic-layer
 * contract for non-UI callers).
 *
 * @param {number | null | undefined} aov - Average order value.
 * @param {number | null | undefined} grossMarginPct - Gross margin percent.
 * @returns {number | null} Break-even CPA, or null when AOV is missing/negative or margin is missing/negative/above 100.
 */
export function breakEvenCpa(
  aov: number | null | undefined,
  grossMarginPct: number | null | undefined,
): number | null {
  if (!isNonNegativeNumber(aov) || !isNonNegativeNumber(grossMarginPct)) return null
  if (grossMarginPct > 100) return null
  return aov * (grossMarginPct / 100)
}

/**
 * Target CAC from lifetime value and a desired LTV:CAC multiple.
 *
 * This is a fully loaded acquisition ceiling (CAC), not media-only CPA.
 * Callers must label it as target CAC so it is not compared to platform CPA
 * as if they were the same number.
 *
 * @param {number | null | undefined} ltv - Expected lifetime value.
 * @param {number | null | undefined} targetLtvCacRatio - Target multiple (3 means 3:1).
 * @returns {number | null} Target CAC ceiling, or null when inputs are not positive.
 */
export function targetCpaFromLtv(
  ltv: number | null | undefined,
  targetLtvCacRatio: number | null | undefined,
): number | null {
  if (!isPositiveNumber(ltv) || !isPositiveNumber(targetLtvCacRatio)) return null
  return ltv / targetLtvCacRatio
}

/**
 * Target CPA from AOV and a desired ROAS: CPA = AOV ÷ ROAS.
 *
 * @param {number | null | undefined} aov - Average order value.
 * @param {number | null | undefined} targetRoas - Target ROAS multiple.
 * @returns {number | null} Target CPA, or null when AOV is missing/negative or ROAS is not positive.
 */
export function targetCpaFromRoas(
  aov: number | null | undefined,
  targetRoas: number | null | undefined,
): number | null {
  if (!isNonNegativeNumber(aov) || !isPositiveNumber(targetRoas)) return null
  return aov / targetRoas
}

/**
 * Classify the realized CPA against break-even (preferred) or the ROAS
 * target. LTV ÷ LTV:CAC is a fully loaded CAC and is never a media-CPA
 * ceiling. A 2% band around the comparison point counts as on-target so
 * floating-point equality does not flicker the verdict. The comparison
 * includes a 1e-9 epsilon because values such as `32 * 1.02`
 * are not exactly 2% in IEEE remainder (`|32.64 − 32| / 32 > 0.02`).
 *
 * @param {number | null} cpa - Realized or funnel CPA. Explicit $0 is classifiable.
 * @param {number | null} breakEven - Contribution-margin ceiling.
 * @param {number | null} targetRoas - ROAS-derived media-CPA ceiling.
 * @returns {CpaVerdict} Qualitative verdict for the result panel.
 */
export function classifyCpaVerdict(
  cpa: number | null,
  breakEven: number | null,
  targetRoas: number | null,
): CpaVerdict {
  const ceiling = resolveCpaCeiling(breakEven, targetRoas)
  if (!isNonNegativeNumber(cpa) || !isNonNegativeNumber(ceiling)) return 'insufficient'
  if (ceiling <= POSITIVE_EPSILON) return cpa <= POSITIVE_EPSILON ? 'on_target' : 'unprofitable'
  const delta = Math.abs(cpa - ceiling) / ceiling
  if (delta <= ON_TARGET_RELATIVE_BAND + POSITIVE_EPSILON) return 'on_target'
  return cpa < ceiling ? 'profitable' : 'unprofitable'
}

/**
 * Which media-CPA ceiling a verdict is allowed to describe. Callers must
 * not say “break-even” when contribution margin was never computed, and
 * must not treat target CAC as a media-CPA ceiling.
 *
 * @param {number | null} breakEven - Contribution-margin ceiling.
 * @param {number | null} targetRoas - ROAS-derived media-CPA ceiling.
 * @returns {CpaVerdictBasis} Comparison basis for verdict copy.
 */
export function resolveCpaVerdictBasis(
  breakEven: number | null,
  targetRoas: number | null,
): CpaVerdictBasis {
  if (isNonNegativeNumber(breakEven)) return 'break_even'
  if (isNonNegativeNumber(targetRoas)) return 'target'
  return 'none'
}

/**
 * The media-CPA ceiling {@link classifyCpaVerdict} compares against:
 * break-even first, otherwise the ROAS target. Target CAC is excluded.
 *
 * @param {number | null} breakEven - Contribution-margin ceiling.
 * @param {number | null} targetRoas - ROAS-derived media-CPA ceiling.
 * @returns {number | null} Comparison ceiling, or null when none exist.
 */
export function resolveCpaCeiling(
  breakEven: number | null,
  targetRoas: number | null,
): number | null {
  if (isNonNegativeNumber(breakEven)) return breakEven
  if (isNonNegativeNumber(targetRoas)) return targetRoas
  return null
}

/**
 * Tightest media-CPA ceiling among break-even and ROAS targets.
 *
 * Used as the target-mode hero number when no realized/funnel CPA exists.
 * LTV ÷ LTV:CAC is a fully loaded CAC and is never mixed into this hero.
 *
 * @param {number | null} breakEven - Contribution-margin ceiling.
 * @param {number | null} targetRoas - ROAS-derived media-CPA ceiling.
 * @returns {number | null} The minimum media ceiling, or null when none exist.
 */
export function tightestMediaCpaCeiling(
  breakEven: number | null,
  targetRoas: number | null,
): number | null {
  const candidates = [breakEven, targetRoas].filter(isNonNegativeNumber)
  if (candidates.length === 0) return null
  return Math.min(...candidates)
}

/**
 * Evaluate the active mode and any optional target/break-even overlays.
 *
 * @param {CpaInputs} inputs - Mode plus the fields that mode consumes.
 * @returns {CpaResults} Derived figures with nulls for missing inputs.
 * @example
 * calculateCpa({ mode: 'realized', adSpend: 1000, conversions: 25 })
 * // { cpa: 40, ... }
 */
export function calculateCpa(inputs: CpaInputs): CpaResults {
  let cpa: number | null = null
  let impliedCpc: number | null = null

  switch (inputs.mode) {
    case 'realized':
      // Pass missing numerators through. `?? 0` would turn a cleared Ad spend
      // field into a $0 CPA / profitable verdict (`r3788181483`).
      cpa = realizedCpa(inputs.adSpend, inputs.conversions)
      break
    case 'funnel':
      if ((inputs.funnelPath ?? 'cpc') === 'cpm') {
        cpa = funnelCpaFromCpm(inputs.cpm, inputs.ctrPct, inputs.cvrPct)
        impliedCpc = impliedCpcFromCpm(inputs.cpm, inputs.ctrPct)
      } else {
        cpa = funnelCpaFromCpc(inputs.cpc, inputs.cvrPct)
        impliedCpc = isNonNegativeNumber(inputs.cpc) ? inputs.cpc : null
      }
      break
    case 'target':
      cpa = null
      break
    default: {
      const exhaustive: never = inputs.mode
      throw new Error(`Unhandled CPA mode: ${String(exhaustive)}`)
    }
  }

  const breakEven = breakEvenCpa(inputs.aov, inputs.grossMarginPct)
  const fromLtv = targetCpaFromLtv(inputs.ltv, inputs.targetLtvCacRatio)
  const fromRoas = targetCpaFromRoas(inputs.aov, inputs.targetRoas)
  const impliedRoas =
    isPositiveNumber(cpa) && isNonNegativeNumber(inputs.aov) ? inputs.aov / cpa : null
  const headroom =
    isNonNegativeNumber(cpa) && isNonNegativeNumber(breakEven) ? breakEven - cpa : null

  return {
    cpa,
    breakEvenCpa: breakEven,
    targetCpaFromLtv: fromLtv,
    targetCpaFromRoas: fromRoas,
    impliedRoas,
    impliedCpc,
    headroom,
    verdict: classifyCpaVerdict(cpa, breakEven, fromRoas),
    verdictBasis: resolveCpaVerdictBasis(breakEven, fromRoas),
  }
}

/**
 * Sensitivity series: CPA across a CVR sweep at a fixed CPC (or implied CPC).
 *
 * @param {number} cpc - Held-constant cost per click.
 * @param {readonly number[]} cvrPctPoints - Conversion-rate percents to evaluate.
 * @returns {Array<{ cvrPct: number, cpa: number | null }>} Points for the chart.
 */
export function cpaSensitivityByCvr(
  cpc: number | null,
  cvrPctPoints: readonly number[],
): Array<{ cvrPct: number; cpa: number | null }> {
  if (!isNonNegativeNumber(cpc)) {
    return cvrPctPoints.map((cvrPct) => ({ cvrPct, cpa: null }))
  }
  return cvrPctPoints.map((cvrPct) => ({
    cvrPct,
    cpa: funnelCpaFromCpc(cpc, cvrPct),
  }))
}

/**
 * Default CVR sweep used by the sensitivity chart (0.5% through 8%).
 *
 * @constant
 */
export const DEFAULT_CVR_SWEEP: readonly number[] = [0.5, 1, 1.5, 2, 3, 4, 5, 6, 8]

/**
 * Nearest cited benchmark to a realized CPA, used to highlight the track.
 *
 * @param {number | null} cpa - Realized or funnel CPA.
 * @param {readonly CpaBenchmark[]} [benchmarks=CPA_BENCHMARKS] - Band set.
 * @returns {CpaBenchmark | null} Closest median, or null when CPA is missing.
 */
export function nearestBenchmark(
  cpa: number | null,
  benchmarks: readonly CpaBenchmark[] = CPA_BENCHMARKS,
): CpaBenchmark | null {
  if (!isPositiveNumber(cpa) || benchmarks.length === 0) return null
  return benchmarks.reduce((best, current) => {
    const bestDelta = Math.abs(best.median - cpa)
    const currentDelta = Math.abs(current.median - cpa)
    return currentDelta < bestDelta ? current : best
  })
}

/**
 * Whether a cited median sits inside the published low–high span.
 *
 * Google ecommerce purchase CPA is the known exception: the all-industry
 * median can sit below the named-industry floor. Visualizations must not
 * assume `low ≤ median ≤ high`.
 *
 * @param {CpaBenchmark} band - Cited band.
 * @returns {boolean} True when the median is inside the named span.
 */
export function isMedianInsideSpan(band: CpaBenchmark): boolean {
  return band.median >= band.low && band.median <= band.high
}

/**
 * User-facing label for a publisher's reported statistic.
 *
 * @param {CpaPublishedStatistic} statistic - Average or median.
 * @returns {string} Lowercase label for tables, tracks, and CSV.
 */
export function publishedStatisticLabel(statistic: CpaPublishedStatistic): string {
  switch (statistic) {
    case 'average':
      return 'average'
    case 'median':
      return 'median'
    default: {
      const exhaustive: never = statistic
      throw new Error(`Unhandled published statistic: ${String(exhaustive)}`)
    }
  }
}

/**
 * CPC the sensitivity chart may hold constant. Returns null when the
 * active mode cannot derive CPC from visible inputs, so the chart does
 * not silently reuse a hidden CPC-path default.
 *
 * @param {CpaMode} mode - Active calculator mode.
 * @param {FunnelPath} funnelPath - Funnel entry path.
 * @param {number | null} cpc - Visible CPC (funnel CPC path).
 * @param {number | null} cpm - Visible CPM (funnel CPM path).
 * @param {number | null} ctrPct - Visible CTR (funnel CPM path).
 * @param {number | null} realizedCpa - Realized or funnel CPA already computed.
 * @param {number | null} cvrPct - Visible CVR used to back out CPC in realized mode.
 * @returns {number | null} Chart CPC, or null when the chart must stay hidden.
 */
export function resolveSensitivityCpc(
  mode: CpaMode,
  funnelPath: FunnelPath,
  cpc: number | null,
  cpm: number | null,
  ctrPct: number | null,
  realizedCpa: number | null,
  cvrPct: number | null,
): number | null {
  switch (mode) {
    case 'target':
      return null
    case 'funnel':
      if (funnelPath === 'cpm') {
        if (cpm == null || ctrPct == null) return null
        return impliedCpcFromCpm(cpm, ctrPct)
      }
      return isNonNegativeNumber(cpc) ? cpc : null
    case 'realized':
      if (isNonNegativeNumber(realizedCpa) && isPositiveNumber(cvrPct)) {
        return realizedCpa * (cvrPct / 100)
      }
      return null
    default: {
      const exhaustive: never = mode
      throw new Error(`Unhandled CPA mode: ${String(exhaustive)}`)
    }
  }
}

/**
 * Parse a numeric field, treating empty as missing and clamping to bounds.
 *
 * HTML `min`/`max` only mark the input invalid — they do not stop a
 * controlled field from storing 150% margin. This is the calculation-safe
 * parse used by the widget.
 *
 * @param {string} raw - Raw input value.
 * @param {number} min - Inclusive lower bound.
 * @param {number} [max] - Optional inclusive upper bound.
 * @returns {number | null} Clamped number, or null when the field is empty.
 */
export function clampNumericInput(raw: string, min: number, max?: number): number | null {
  if (raw.trim() === '') return null
  const next = Number(raw)
  if (!Number.isFinite(next)) return null
  if (next < min) return min
  if (max != null && next > max) return max
  return next
}

/**
 * Inputs plus results needed to build a mode-gated CSV export.
 *
 * @interface CpaExportSnapshot
 * @property {CpaMode} mode - Active calculator mode.
 * @property {FunnelPath} funnelPath - Funnel entry path (ignored unless mode is funnel).
 * @property {number | null} adSpend - Realized-mode spend.
 * @property {number | null} conversions - Realized-mode conversions.
 * @property {number | null} cpc - Funnel CPC-path cost per click.
 * @property {number | null} cpm - Funnel CPM-path cost per thousand.
 * @property {number | null} ctrPct - Funnel CPM-path click-through rate.
 * @property {number | null} cvrPct - Funnel conversion rate.
 * @property {number | null} aov - Average order value overlay.
 * @property {number | null} grossMarginPct - Gross margin overlay.
 * @property {number | null} ltv - Lifetime value overlay.
 * @property {number | null} targetLtvCacRatio - Target LTV:CAC overlay.
 * @property {number | null} targetRoas - Target ROAS overlay.
 * @property {CpaResults} results - Derived figures for the snapshot.
 */
export interface CpaExportSnapshot {
  mode: CpaMode
  funnelPath: FunnelPath
  adSpend: number | null
  conversions: number | null
  cpc: number | null
  cpm: number | null
  ctrPct: number | null
  cvrPct: number | null
  aov: number | null
  grossMarginPct: number | null
  ltv: number | null
  targetLtvCacRatio: number | null
  targetRoas: number | null
  results: CpaResults
}

/**
 * Formats a verdict with the ceiling it was compared against.
 *
 * @param {CpaVerdict} verdict - Qualitative read.
 * @param {CpaVerdictBasis} basis - Ceiling the math used.
 * @returns {string} Export cell that cannot be read as a break-even claim when it was not.
 * @example
 * formatCpaVerdictForExport('profitable', 'target')
 * // 'profitable (ROAS target)'
 */
export function formatCpaVerdictForExport(verdict: CpaVerdict, basis: CpaVerdictBasis): string {
  switch (verdict) {
    case 'insufficient':
      return 'insufficient'
    case 'profitable':
    case 'unprofitable':
    case 'on_target': {
      switch (basis) {
        case 'break_even':
          return `${verdict} (break-even)`
        case 'target':
          return `${verdict} (ROAS target)`
        case 'none':
          return verdict
        default: {
          const exhaustive: never = basis
          throw new Error(`Unhandled verdict basis: ${String(exhaustive)}`)
        }
      }
    }
    default: {
      const exhaustive: never = verdict
      throw new Error(`Unhandled verdict: ${String(exhaustive)}`)
    }
  }
}

/**
 * Builds CSV rows that only include inputs visible in the active mode.
 *
 * Hidden-mode defaults (spend in target mode, CPC in realized mode) stay out
 * of the file so an export matches what the operator actually entered.
 *
 * @param {CpaExportSnapshot} snapshot - Mode, visible inputs, and results.
 * @returns {Array<Array<string | number>>} Rows for {@link downloadCsv}.
 */
export function buildCpaExportRows(snapshot: CpaExportSnapshot): Array<Array<string | number>> {
  const { mode, funnelPath, results } = snapshot
  const cell = (value: number | null): string | number => value ?? ''
  const rows: Array<Array<string | number>> = [
    ['Field', 'Value'],
    ['Mode', mode],
  ]

  switch (mode) {
    case 'realized':
      rows.push(['Ad spend', cell(snapshot.adSpend)], ['Conversions', cell(snapshot.conversions)])
      break
    case 'funnel':
      rows.push(['Funnel path', funnelPath])
      if (funnelPath === 'cpm') {
        rows.push(['CPM', cell(snapshot.cpm)], ['CTR %', cell(snapshot.ctrPct)])
      } else {
        rows.push(['CPC', cell(snapshot.cpc)])
      }
      rows.push(['CVR %', cell(snapshot.cvrPct)])
      break
    case 'target':
      break
    default: {
      const exhaustive: never = mode
      throw new Error(`Unhandled CPA mode: ${String(exhaustive)}`)
    }
  }

  rows.push(
    ['AOV', cell(snapshot.aov)],
    ['Gross margin %', cell(snapshot.grossMarginPct)],
    ['LTV', cell(snapshot.ltv)],
    ['Target LTV:CAC', cell(snapshot.targetLtvCacRatio)],
    ['Target ROAS', cell(snapshot.targetRoas)],
    ['CPA', cell(results.cpa)],
    ['Break-even CPA', cell(results.breakEvenCpa)],
    ['Target CAC from LTV', cell(results.targetCpaFromLtv)],
    ['Target CPA from ROAS', cell(results.targetCpaFromRoas)],
    ['Implied ROAS', cell(results.impliedRoas)],
    ['Verdict', formatCpaVerdictForExport(results.verdict, results.verdictBasis)],
    ['Verdict basis', results.verdictBasis],
    [],
    ['Benchmark', 'Low', 'Published figure', 'Statistic', 'High', 'Source'],
    ...CPA_BENCHMARKS.map((band) => [
      band.label,
      band.low,
      band.median,
      publishedStatisticLabel(band.statistic),
      band.high,
      band.source,
    ]),
  )
  return rows
}
